import React, { useState, useRef } from 'react';

const AddImageModal = ({ isOpen, onClose, onAddImage }) => {
  const [formData, setFormData] = useState({
    title: '',
    category: 'Living',
    imageUrl: ''
  });
  const [isDragging, setIsDragging] = useState(false);
  const fileInputRef = useRef(null);

  const categories = ['Living', 'Bedroom', 'Kitchen', 'Office', 'Bathroom'];

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleFileSelect = (file) => {
    if (file && file.type.startsWith('image/')) {
      const reader = new FileReader();
      reader.onload = (e) => {
        setFormData(prev => ({ ...prev, imageUrl: e.target.result }));
      };
      reader.readAsDataURL(file);
    }
  };

  const handleDrop = (e) => {
    e.preventDefault();
    setIsDragging(false);
    const file = e.dataTransfer.files[0];
    handleFileSelect(file);
  };

  const handleDragOver = (e) => {
    e.preventDefault();
    setIsDragging(true);
  };

  const handleDragLeave = () => {
    setIsDragging(false);
  };

  const handleFileInput = (e) => {
    const file = e.target.files[0];
    handleFileSelect(file);
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    if (formData.title && formData.category && formData.imageUrl) {
      onAddImage({
        id: Date.now(),
        ...formData
      });
      setFormData({
        title: '',
        category: 'Living',
        imageUrl: ''
      });
      onClose();
    }
  };

  const handleClose = () => {
    setFormData({
      title: '',
      category: 'Living',
      imageUrl: ''
    });
    onClose();
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm p-4">
      <div className="bg-white rounded-2xl max-w-lg w-full max-h-[90vh] overflow-y-auto" style={{boxShadow: '0 20px 25px -5px rgba(0, 0, 0, 0.1)'}}>
        {/* Header */}
        <div className="flex justify-between items-center p-6 border-b border-stone-200">
          <h2 className="text-2xl font-bold text-stone-900">Add New Image</h2>
          <button
            onClick={handleClose}
            className="text-stone-400 hover:text-stone-600 transition-colors"
          >
            <span className="material-symbols-outlined text-2xl">close</span>
          </button>
        </div>

        {/* Form */}
        <form onSubmit={handleSubmit} className="p-6 space-y-6">
          {/* Image Upload */}
          <div>
            <label className="block text-sm font-medium text-stone-700 mb-2">
              Image
            </label>
            <div
              className={`border-2 border-dashed rounded-xl p-8 text-center transition-colors cursor-pointer ${
                isDragging
                  ? 'border-primary bg-primary/5'
                  : 'border-stone-300 hover:border-stone-400'
              }`}
              onDrop={handleDrop}
              onDragOver={handleDragOver}
              onDragLeave={handleDragLeave}
              onClick={() => fileInputRef.current?.click()}
            >
              {formData.imageUrl ? (
                <div className="space-y-4">
                  <img
                    src={formData.imageUrl}
                    alt="Preview"
                    className="mx-auto max-h-48 rounded-lg object-cover"
                  />
                  <p className="text-sm text-stone-600">Click or drag to replace image</p>
                </div>
              ) : (
                <div className="space-y-4">
                  <span className="material-symbols-outlined text-4xl text-stone-400">
                    cloud_upload
                  </span>
                  <div>
                    <p className="text-stone-900 font-medium">Drop image here or click to browse</p>
                    <p className="text-stone-500 text-sm">PNG, JPG, GIF up to 10MB</p>
                  </div>
                </div>
              )}
              <input
                ref={fileInputRef}
                type="file"
                accept="image/*"
                onChange={handleFileInput}
                className="hidden"
              />
            </div>
          </div>

          {/* Title */}
          <div>
            <label htmlFor="title" className="block text-sm font-medium text-stone-700 mb-2">
              Title
            </label>
            <input
              type="text"
              id="title"
              name="title"
              value={formData.title}
              onChange={handleInputChange}
              required
              className="w-full px-4 py-2 border border-stone-300 rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent"
              placeholder="Enter image title"
            />
          </div>

          {/* Category */}
          <div>
            <label htmlFor="category" className="block text-sm font-medium text-stone-700 mb-2">
              Category
            </label>
            <select
              id="category"
              name="category"
              value={formData.category}
              onChange={handleInputChange}
              required
              className="w-full px-4 py-2 border border-stone-300 rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent"
            >
              {categories.map(category => (
                <option key={category} value={category}>
                  {category}
                </option>
              ))}
            </select>
          </div>

          {/* Actions */}
          <div className="flex gap-4 pt-4">
            <button
              type="button"
              onClick={handleClose}
              className="flex-1 px-4 py-2 border border-stone-300 text-stone-700 rounded-lg hover:bg-stone-50 transition-colors"
            >
              Cancel
            </button>
            <button
              type="submit"
              className="flex-1 px-4 py-2 text-white rounded-lg hover:opacity-90 transition-opacity"
              style={{backgroundColor: '#C8A96A'}}
            >
              Add Image
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default AddImageModal;
